import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useIsMobile } from '../../components/Layout/ResponsiveSidebar';
import PageHeader from '../../components/common/PageHeader';
import StatCard from '../../components/common/StatCard';
import StatusBadge from '../../components/common/StatusBadge';
import TodaysCommunications from '../../components/common/TodaysCommunications';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { formatDateTime, formatDate, formatTime, cn, getShiftTypeClass } from '../../lib/utils';
import { 
  Calendar, Clock, Users, FileText, 
  CheckCircle, XCircle, AlertTriangle, Play, Square, 
  Moon, MapPin, Timer, AlertCircle, ChevronRight, BookOpen, RefreshCw, Briefcase, Hand
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  QuickActionButton,
  MobileListItem,
  MobileSectionHeader,
} from '../../components/common/MobileComponents';

const WorkerDashboard = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const { isMobile, isTablet } = useIsMobile();
  const [stats, setStats] = useState({});
  const [upcomingShifts, setUpcomingShifts] = useState([]);
  const [offeredShifts, setOfferedShifts] = useState([]);
  const [openShifts, setOpenShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Time Clock State
  const [activeClock, setActiveClock] = useState(null);
  const [eligibleShifts, setEligibleShifts] = useState([]);
  const [clockInDialogOpen, setClockInDialogOpen] = useState(false);
  const [clockOutDialogOpen, setClockOutDialogOpen] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);
  const [clockNotes, setClockNotes] = useState('');
  const [elapsedTime, setElapsedTime] = useState('00:00:00');

  // Fetch dashboard data
  const fetchDashboardData = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);
    try {
      const [statsRes, shiftsRes, activeClockRes, eligibleRes, openShiftsRes] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/shifts', { params: { worker_id: user.id } }),
        api.get('/time-clock/active'),
        api.get('/time-clock/eligible-shifts'),
        api.get('/shifts/open')
      ]);

      setStats(statsRes.data);
      setActiveClock(activeClockRes.data.active ? activeClockRes.data.record : null);
      setEligibleShifts(eligibleRes.data);
      setOpenShifts((openShiftsRes.data || []).filter(s => new Date(s.start_time) >= new Date()));
      
      const now = new Date();
      // Statuses that indicate a completed/cancelled shift - exclude from pending
      const completedStatuses = ['completed', 'cancelled', 'in_progress'];
      
      const upcoming = shiftsRes.data
        .filter(s => new Date(s.start_time) >= now && ['accepted', 'scheduled'].includes(s.status))
        .slice(0, 5);
      
      // Show shifts that workers need to respond to - any status except completed/cancelled/accepted
      const offered = shiftsRes.data
        .filter(s => {
          const status = s.status || '';
          const isBlocked = completedStatuses.includes(status) || status === 'accepted';
          return !isBlocked && new Date(s.start_time) >= now;
        })
        .slice(0, 10);
      
      setUpcomingShifts(upcoming);
      setOfferedShifts(offered);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id]);

  useEffect(() => {
    fetchDashboardData();
    
    // Refresh every 30 seconds for clock status
    const interval = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  // Update elapsed time counter
  useEffect(() => {
    if (!activeClock?.clock_in_time) return;
    
    const updateElapsed = () => {
      const start = new Date(activeClock.clock_in_time);
      const now = new Date();
      const diff = Math.floor((now - start) / 1000);
      
      const hours = Math.floor(diff / 3600);
      const minutes = Math.floor((diff % 3600) / 60);
      const seconds = diff % 60;
      
      setElapsedTime(
        `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
      );
    };
    
    updateElapsed();
    const timer = setInterval(updateElapsed, 1000);
    return () => clearInterval(timer);
  }, [activeClock]);

  const handleShiftResponse = async (shiftId, response) => {
    try {
      await api.post(`/shifts/${shiftId}/respond`, null, {
        params: { response }
      });
      toast.success(`Shift ${response}ed`);
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to respond to shift');
    }
  };

  const handleClaimShift = async (shiftId) => {
    try {
      await api.post(`/shifts/${shiftId}/claim`);
      toast.success('Shift claimed successfully! It has been added to your schedule.');
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to claim shift');
    }
  };

  const handleClockIn = async () => {
    if (!selectedShift) return;
    
    try {
      const response = await api.post('/time-clock/clock-in', {
        shift_id: selectedShift.id,
        notes: clockNotes
      });
      
      toast.success(response.data.message);
      
      // Show warnings if applicable
      if (response.data.warnings?.is_early) {
        toast.warning(`Early clock-in: ${Math.abs(response.data.warnings.variance_minutes)} minutes before scheduled start`);
      }
      if (response.data.warnings?.is_late) {
        toast.warning(`Late clock-in: ${response.data.warnings.variance_minutes} minutes after scheduled start`);
      }
      
      if (response.data.is_overnight) {
        toast.info('This overnight shift has been split into Active (8h) and Sleepover (4h) segments');
      }
      
      setClockInDialogOpen(false);
      setClockNotes('');
      setSelectedShift(null);
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clock in');
    }
  };

  const handleClockOut = async () => {
    if (!activeClock) return;
    
    try {
      const shiftId = activeClock.parent_shift_id || activeClock.shift_id;
      const response = await api.post('/time-clock/clock-out', {
        shift_id: shiftId,
        notes: clockNotes
      });
      
      toast.success(response.data.message);
      
      // Show warnings if applicable
      if (response.data.variance?.is_early_out) {
        toast.warning(`Early clock-out: ${Math.abs(response.data.variance.variance_minutes)} minutes before scheduled end`);
      }
      if (response.data.variance?.is_late_out) {
        toast.warning(`Late clock-out: ${response.data.variance.variance_minutes} minutes after scheduled end`);
      }
      
      setClockOutDialogOpen(false);
      setClockNotes('');
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clock out');
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return '';
    return new Date(isoString).toLocaleTimeString('en-AU', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getNextShift = () => {
    if (upcomingShifts.length === 0) return null;
    return upcomingShifts[0];
  };

  const nextShift = getNextShift();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  // Mobile Layout
  if (isMobile || isTablet) {
    return (
      <div className={cn("space-y-4 animate-fade-in", isMobile && "pb-20")} data-testid="worker-dashboard">
        {/* Mobile Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">Hi, {user?.first_name}!</h1>
            <p className="text-sm text-muted-foreground">
              {new Date().toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'short' })}
            </p>
          </div>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => fetchDashboardData(true)}
            disabled={refreshing}
            className="touch-manipulation"
          >
            <RefreshCw className={cn("h-5 w-5", refreshing && "animate-spin")} />
          </Button>
        </div>

        {/* PROMINENT CLOCK IN/OUT SECTION - Always visible on mobile */}
        {!activeClock ? (
          <Card className="border-2 border-primary/50 bg-primary/5">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-primary/20">
                    <Play className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold">Clock In</p>
                    <p className="text-sm text-muted-foreground">
                      {eligibleShifts.length > 0 
                        ? `${eligibleShifts.length} shift(s) ready` 
                        : 'No shifts in clock window'}
                    </p>
                  </div>
                </div>
              </div>
              
              {eligibleShifts.length > 0 ? (
                <div className="space-y-2">
                  {eligibleShifts.slice(0, 3).map(shift => (
                    <div 
                      key={shift.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-background/50 border"
                    >
                      <div>
                        <p className="font-medium text-sm">{shift.house_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {shift.shift_type} • {formatTime(shift.start_time)} - {formatTime(shift.end_time)}
                        </p>
                      </div>
                      <Button 
                        size="sm"
                        className="h-10 px-4 touch-manipulation bg-green-600 hover:bg-green-700"
                        onClick={() => {
                          setSelectedShift(shift);
                          setClockInDialogOpen(true);
                        }}
                        data-testid={`clock-in-mobile-${shift.id}`}
                      >
                        <Play className="h-4 w-4 mr-1" />
                        Start
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-muted-foreground">
                  <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No shifts available to clock into right now.</p>
                  <p className="text-xs mt-1">Shifts appear 15 minutes before start time.</p>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card className="border-2 border-green-500/50 bg-green-500/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-full bg-green-500/20 animate-pulse">
                  <Timer className="h-6 w-6 text-green-500" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-green-600 dark:text-green-400">On Shift</p>
                  <p className="text-sm text-muted-foreground">{activeClock.house_name}</p>
                </div>
                {activeClock.is_overnight && (
                  <Badge className="bg-indigo-500/20 text-indigo-500">
                    <Moon className="h-3 w-3 mr-1" />
                    {activeClock.segment_label || 'Overnight'}
                  </Badge>
                )}
              </div>
              
              <div className="text-center py-3">
                <div className="text-4xl font-mono font-bold text-green-500 mb-1">{elapsedTime}</div>
                <p className="text-xs text-muted-foreground">
                  {activeClock.shift_type} • Until {formatTime(activeClock.rostered_end)}
                </p>
              </div>
              
              <Button 
                className="w-full h-12 bg-red-500 hover:bg-red-600 touch-manipulation"
                onClick={() => setClockOutDialogOpen(true)}
                data-testid="clock-out-mobile-btn"
              >
                <Square className="h-5 w-5 mr-2" />
                Clock Out
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions - Mobile Grid */}
        <div>
          <MobileSectionHeader title="Quick Actions" />
          <div className="grid grid-cols-4 gap-2">
            <QuickActionButton icon={FileText} label="Add Note" onClick={() => navigate('/worker/notes')} />
            <QuickActionButton icon={BookOpen} label="Diary" onClick={() => navigate('/worker/diary')} />
            <QuickActionButton icon={Calendar} label="Roster" onClick={() => navigate('/worker/roster')} />
            <QuickActionButton icon={Users} label="Clients" onClick={() => navigate('/worker/clients')} />
          </div>
        </div>

        {/* Communications */}
        <TodaysCommunications basePath="/worker" />

        {/* Stats - Mobile */}
        <div className="grid grid-cols-2 gap-3">
          <Card className="p-3">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-primary" />
              <span className="text-xs text-muted-foreground">Today</span>
            </div>
            <p className="text-2xl font-bold mt-1">{stats.my_shifts_today || 0}</p>
            <p className="text-xs text-muted-foreground">shifts</p>
          </Card>
          <Card className="p-3">
            <div className="flex items-center gap-2">
              <Timer className="h-4 w-4 text-primary" />
              <span className="text-xs text-muted-foreground">This Week</span>
            </div>
            <p className="text-2xl font-bold mt-1">{stats.my_hours_this_week || 0}h</p>
            <p className="text-xs text-muted-foreground">worked</p>
          </Card>
        </div>

        {/* Pending Shifts - Mobile */}
        {offeredShifts.length > 0 && (
          <div>
            <MobileSectionHeader title="Pending Response" subtitle={`${offeredShifts.length} shift(s)`} />
            <div className="space-y-2">
              {offeredShifts.map(shift => (
                <Card key={shift.id} className="overflow-hidden">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <Badge className={cn("mb-2", getShiftTypeClass(shift.shift_type))}>{shift.shift_type}</Badge>
                        <p className="font-semibold">{shift.house_name}</p>
                        <p className="text-sm text-muted-foreground">{formatDate(shift.start_time)}</p>
                        <p className="text-xs text-muted-foreground">{formatTime(shift.start_time)} - {formatTime(shift.end_time)}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" className="flex-1 h-11 text-red-500 hover:bg-red-500/10" onClick={() => handleShiftResponse(shift.id, 'decline')}>
                        <XCircle className="h-4 w-4 mr-1" />Decline
                      </Button>
                      <Button className="flex-1 h-11" onClick={() => handleShiftResponse(shift.id, 'accept')}>
                        <CheckCircle className="h-4 w-4 mr-1" />Accept
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Next Shift - Mobile */}
        {nextShift && !activeClock && (
          <div>
            <MobileSectionHeader 
              title="Next Shift" 
              action={<Button variant="ghost" size="sm" onClick={() => navigate('/worker/roster')}>View All<ChevronRight className="h-4 w-4 ml-1" /></Button>}
            />
            <MobileListItem
              icon={Calendar}
              title={nextShift.house_name}
              subtitle={`${formatDate(nextShift.start_time)} • ${nextShift.shift_type}`}
              badge={<Badge className={cn("text-xs", getShiftTypeClass(nextShift.shift_type))}>{formatTime(nextShift.start_time)}</Badge>}
              onClick={() => navigate('/worker/roster')}
            />
          </div>
        )}

        {/* Open Shifts - Mobile */}
        {openShifts.length > 0 && (
          <div>
            <MobileSectionHeader 
              title="Open Shifts" 
              subtitle={`${openShifts.length} available`}
            />
            <div className="space-y-2">
              {openShifts.slice(0, 5).map(shift => (
                <Card key={shift.id} className="border-green-500/30 bg-green-500/5" data-testid={`open-shift-mobile-${shift.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <MapPin className="h-4 w-4 text-green-600" />
                          <p className="font-medium">{shift.house_name}</p>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {formatDate(shift.start_time)} • {shift.shift_type}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatTime(shift.start_time)} - {formatTime(shift.end_time)}
                        </p>
                      </div>
                      <Badge variant="outline" className="bg-green-500/20 text-green-600 border-green-500/30">
                        Open
                      </Badge>
                    </div>
                    <Button 
                      size="sm"
                      className="w-full bg-green-600 hover:bg-green-700"
                      onClick={() => handleClaimShift(shift.id)}
                      data-testid={`claim-shift-mobile-${shift.id}`}
                    >
                      <Hand className="h-4 w-4 mr-2" />
                      Claim This Shift
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Clock Dialogs */}
        <Dialog open={clockInDialogOpen} onOpenChange={setClockInDialogOpen}>
          <DialogContent className="sm:max-w-md mx-4">
            <DialogHeader>
              <DialogTitle>Clock In</DialogTitle>
              <DialogDescription>{selectedShift?.house_name} - {selectedShift?.shift_type}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="bg-muted/50 rounded-lg p-3 text-sm">
                <p><strong>Scheduled:</strong> {formatTime(selectedShift?.start_time)} - {formatTime(selectedShift?.end_time)}</p>
              </div>
              <div>
                <label className="text-sm font-medium block mb-1.5">Notes (optional)</label>
                <Textarea value={clockNotes} onChange={(e) => setClockNotes(e.target.value)} placeholder="Add any notes..." className="h-24 resize-none" />
              </div>
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={() => setClockInDialogOpen(false)} className="w-full sm:w-auto">Cancel</Button>
              <Button onClick={handleClockIn} className="w-full sm:w-auto h-11"><Play className="h-4 w-4 mr-2" />Clock In Now</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={clockOutDialogOpen} onOpenChange={setClockOutDialogOpen}>
          <DialogContent className="sm:max-w-md mx-4">
            <DialogHeader>
              <DialogTitle>Clock Out</DialogTitle>
              <DialogDescription>{activeClock?.house_name} - Time: {elapsedTime}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="bg-muted/50 rounded-lg p-3 text-sm">
                <p><strong>Started:</strong> {formatTime(activeClock?.clock_in_time)}</p>
                <p><strong>Scheduled End:</strong> {formatTime(activeClock?.rostered_end)}</p>
              </div>
              <div>
                <label className="text-sm font-medium block mb-1.5">Notes (optional)</label>
                <Textarea value={clockNotes} onChange={(e) => setClockNotes(e.target.value)} placeholder="Add any notes..." className="h-24 resize-none" />
              </div>
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={() => setClockOutDialogOpen(false)} className="w-full sm:w-auto">Cancel</Button>
              <Button onClick={handleClockOut} className="w-full sm:w-auto h-11 bg-red-500 hover:bg-red-600"><Square className="h-4 w-4 mr-2" />Clock Out</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Desktop Layout (Original)
  return (
    <div className="space-y-8 animate-fade-in" data-testid="worker-dashboard">
      <PageHeader 
        title={`Welcome back, ${user?.first_name}!`}
        description="Your shift overview and tasks"
      />

      {/* Active Shift Clock Banner */}
      {activeClock && (
        <Card className="border-2 border-green-500/50 bg-green-500/5">
          <CardContent className="py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-green-500/20 animate-pulse">
                  <Timer className="h-8 w-8 text-green-500" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-xl">Currently On Shift</h3>
                    {activeClock.is_overnight && (
                      <Badge className="bg-indigo-500/20 text-indigo-500 border-indigo-500/30">
                        <Moon className="h-3 w-3 mr-1" />
                        {activeClock.segment_label || 'Overnight'}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                    <span>{activeClock.house_name}</span>
                    <span>•</span>
                    <span>{activeClock.shift_type}</span>
                    <span>•</span>
                    <span>Started: {formatTime(activeClock.clock_in_time)}</span>
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-4xl font-mono font-bold text-green-500">
                  {elapsedTime}
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Scheduled until {formatTime(activeClock.rostered_end)}
                </p>
                <Button 
                  className="mt-3 bg-red-500 hover:bg-red-600"
                  onClick={() => setClockOutDialogOpen(true)}
                  data-testid="clock-out-btn"
                >
                  <Square className="h-4 w-4 mr-2" />
                  Clock Out
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Eligible Shifts for Clock-In */}
      {!activeClock && eligibleShifts.length > 0 && (
        <Card className="border-2 border-amber-500/50 bg-amber-500/5">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-500" />
              Ready to Clock In
            </CardTitle>
            <CardDescription>
              You have {eligibleShifts.length} shift(s) available to clock into now
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {eligibleShifts.map(shift => (
              <div 
                key={shift.id}
                className={cn(
                  "p-4 rounded-lg border-2 border-border/40 hover:border-primary/50 transition-colors",
                  getShiftTypeClass(shift.shift_type)
                )}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{shift.house_name}</p>
                      {shift.is_overnight && (
                        <Badge variant="outline" className="border-indigo-500 text-indigo-500">
                          <Moon className="h-3 w-3 mr-1" />
                          Overnight
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {shift.shift_type} • {formatTime(shift.start_time)} - {formatTime(shift.end_time)}
                    </p>
                    {shift.is_overnight && (
                      <p className="text-xs text-indigo-500 mt-1">
                        Will be split: Active (8h) + Sleepover (4h)
                      </p>
                    )}
                    {shift.is_early && (
                      <p className="text-xs text-amber-500 mt-1 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        Early clock-in (before scheduled start)
                      </p>
                    )}
                    {shift.is_late && (
                      <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" />
                        Late clock-in will be logged
                      </p>
                    )}
                  </div>
                  <Button 
                    onClick={() => {
                      setSelectedShift(shift);
                      setClockInDialogOpen(true);
                    }}
                    data-testid={`clock-in-${shift.id}`}
                  >
                    <Play className="h-4 w-4 mr-2" />
                    Clock In
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Today's Key Communications */}
      <TodaysCommunications basePath="/worker" />

      {/* Stats */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Today's Shifts"
          value={stats.my_shifts_today || 0}
          icon={Calendar}
        />
        <StatCard
          title="Pending Shifts"
          value={stats.pending_shifts || offeredShifts.length || 0}
          icon={Clock}
        />
        <StatCard
          title="This Week"
          value={`${stats.my_hours_this_week || 0}h`}
          icon={Timer}
        />
        <StatCard
          title="Notes Submitted"
          value={stats.my_notes_count || 0}
          icon={FileText}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Offered Shifts */}
        <Card className="border-border/40 bg-card/50 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Shifts Awaiting Response
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {offeredShifts.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">No pending shifts</p>
            ) : (
              offeredShifts.map(shift => (
                <div 
                  key={shift.id} 
                  className={cn(
                    "p-4 rounded-lg border border-border/40",
                    getShiftTypeClass(shift.shift_type)
                  )}
                  data-testid={`offered-shift-${shift.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium">{shift.house_name}</p>
                      <p className="text-sm text-muted-foreground">
                        {formatDate(shift.start_time)} • {shift.shift_type}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatTime(shift.start_time)} - {formatTime(shift.end_time)}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-red-500 hover:bg-red-500/10"
                        onClick={() => handleShiftResponse(shift.id, 'decline')}
                        data-testid={`decline-shift-${shift.id}`}
                      >
                        <XCircle className="h-4 w-4" />
                      </Button>
                      <Button 
                        size="sm"
                        onClick={() => handleShiftResponse(shift.id, 'accept')}
                        data-testid={`accept-shift-${shift.id}`}
                      >
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Accept
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Upcoming Shifts */}
        <Card className="border-border/40 bg-card/50 backdrop-blur-sm">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Upcoming Shifts</CardTitle>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/worker/roster')}
              data-testid="view-roster"
            >
              View All
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {upcomingShifts.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">No upcoming shifts</p>
            ) : (
              upcomingShifts.map(shift => (
                <div 
                  key={shift.id} 
                  className={cn(
                    "p-4 rounded-lg border border-border/40",
                    getShiftTypeClass(shift.shift_type)
                  )}
                  data-testid={`upcoming-shift-${shift.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{shift.house_name}</p>
                        {shift.shift_type?.toLowerCase().includes('night') && (
                          <Moon className="h-4 w-4 text-indigo-500" />
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {formatDate(shift.start_time)} • {shift.shift_type}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatTime(shift.start_time)} - {formatTime(shift.end_time)}
                      </p>
                    </div>
                    <StatusBadge status={shift.status} />
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      {/* Open Shifts - Available to Claim */}
      {openShifts.length > 0 && (
        <Card className="border-green-500/30 bg-green-500/5">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-green-500" />
              Open Shifts Available
              <Badge variant="outline" className="ml-2 bg-green-500/20 text-green-600 border-green-500/30">
                {openShifts.length} available
              </Badge>
            </CardTitle>
            <CardDescription>
              These shifts are currently unassigned. Claim one to add it to your schedule.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {openShifts.map(shift => (
                <div 
                  key={shift.id} 
                  className={cn(
                    "p-4 rounded-lg border border-border/40 bg-background/50",
                    getShiftTypeClass(shift.shift_type)
                  )}
                  data-testid={`open-shift-${shift.id}`}
                >
                  <div className="flex flex-col gap-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        <p className="font-medium">{shift.house_name}</p>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        {formatDate(shift.start_time)}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                        <Clock className="h-3 w-3" />
                        {formatTime(shift.start_time)} - {formatTime(shift.end_time)}
                      </div>
                      <Badge variant="outline" className="mt-2">
                        {shift.shift_type}
                        {shift.shift_type?.toLowerCase().includes('night') && (
                          <Moon className="h-3 w-3 ml-1 text-indigo-500" />
                        )}
                      </Badge>
                    </div>
                    <Button 
                      size="sm"
                      className="w-full bg-green-600 hover:bg-green-700"
                      onClick={() => handleClaimShift(shift.id)}
                      data-testid={`claim-shift-${shift.id}`}
                    >
                      <Hand className="h-4 w-4 mr-2" />
                      Claim Shift
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <Card className="border-border/40 bg-card/50 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="text-lg">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Button 
              variant="outline" 
              className="h-auto py-6 flex-col gap-2"
              onClick={() => navigate('/worker/notes')}
              data-testid="quick-add-note"
            >
              <FileText className="h-6 w-6" />
              Add Shift Note
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-6 flex-col gap-2"
              onClick={() => navigate('/worker/incidents')}
              data-testid="quick-report-incident"
            >
              <AlertTriangle className="h-6 w-6" />
              Report Incident
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-6 flex-col gap-2"
              onClick={() => navigate('/worker/diary')}
              data-testid="quick-add-diary"
            >
              <Calendar className="h-6 w-6" />
              Add Diary Post
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-6 flex-col gap-2"
              onClick={() => navigate('/worker/profile')}
              data-testid="quick-my-profile"
            >
              <Users className="h-6 w-6" />
              My Profile
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Clock In Dialog */}
      <Dialog open={clockInDialogOpen} onOpenChange={setClockInDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Play className="h-5 w-5 text-green-500" />
              Clock In to Shift
            </DialogTitle>
            <DialogDescription>
              Confirm your clock-in for this shift
            </DialogDescription>
          </DialogHeader>
          
          {selectedShift && (
            <div className="space-y-4 py-4">
              <div className="p-4 rounded-lg bg-muted/30 border border-border/40">
                <div className="flex items-center gap-2 mb-2">
                  <h4 className="font-semibold">{selectedShift.house_name}</h4>
                  {selectedShift.is_overnight && (
                    <Badge className="bg-indigo-500/20 text-indigo-500">
                      <Moon className="h-3 w-3 mr-1" />
                      Overnight
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  {selectedShift.shift_type} • {formatDate(selectedShift.start_time)}
                </p>
                <p className="text-sm">
                  Scheduled: {formatTime(selectedShift.start_time)} - {formatTime(selectedShift.end_time)}
                </p>
                
                {selectedShift.is_overnight && (
                  <div className="mt-3 p-3 rounded bg-indigo-500/10 border border-indigo-500/20">
                    <p className="text-sm text-indigo-600 dark:text-indigo-400 font-medium">
                      Overnight Shift Split:
                    </p>
                    <ul className="text-xs text-muted-foreground mt-1 space-y-1">
                      <li>• Segment 1: Active Overnight (8 hours) - requires full notes</li>
                      <li>• Segment 2: Sleepover (4 hours) - requires separate notes</li>
                    </ul>
                  </div>
                )}
              </div>

              {selectedShift.is_early && (
                <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-sm">
                  <p className="font-medium text-amber-600 dark:text-amber-400 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    Early Clock-In
                  </p>
                  <p className="text-muted-foreground mt-1">
                    You are clocking in before the scheduled start time. This will be logged.
                  </p>
                </div>
              )}

              {selectedShift.is_late && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-sm">
                  <p className="font-medium text-red-600 dark:text-red-400 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    Late Clock-In
                  </p>
                  <p className="text-muted-foreground mt-1">
                    You are clocking in after the scheduled start time. This will be flagged for review.
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label>Notes (optional)</Label>
                <Textarea 
                  placeholder="Any notes about your clock-in..."
                  value={clockNotes}
                  onChange={(e) => setClockNotes(e.target.value)}
                  rows={2}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setClockInDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleClockIn} className="bg-green-500 hover:bg-green-600">
              <Play className="h-4 w-4 mr-2" />
              Confirm Clock In
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clock Out Dialog */}
      <Dialog open={clockOutDialogOpen} onOpenChange={setClockOutDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Square className="h-5 w-5 text-red-500" />
              Clock Out of Shift
            </DialogTitle>
            <DialogDescription>
              Confirm your clock-out for this shift
            </DialogDescription>
          </DialogHeader>
          
          {activeClock && (
            <div className="space-y-4 py-4">
              <div className="p-4 rounded-lg bg-muted/30 border border-border/40">
                <h4 className="font-semibold">{activeClock.house_name}</h4>
                <p className="text-sm text-muted-foreground">
                  {activeClock.shift_type} 
                  {activeClock.segment_label && ` • ${activeClock.segment_label}`}
                </p>
                <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Clocked in:</span>
                    <p className="font-medium">{formatTime(activeClock.clock_in_time)}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Time worked:</span>
                    <p className="font-medium font-mono">{elapsedTime}</p>
                  </div>
                </div>
                
                {activeClock.is_overnight && activeClock.segment_type === 'active' && (
                  <div className="mt-3 p-3 rounded bg-indigo-500/10 border border-indigo-500/20">
                    <p className="text-sm text-indigo-600 dark:text-indigo-400">
                      After clocking out, the Sleepover segment will automatically begin.
                    </p>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Clock-out Notes (optional)</Label>
                <Textarea 
                  placeholder="Any notes about your shift or clock-out..."
                  value={clockNotes}
                  onChange={(e) => setClockNotes(e.target.value)}
                  rows={2}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setClockOutDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleClockOut} className="bg-red-500 hover:bg-red-600">
              <Square className="h-4 w-4 mr-2" />
              Confirm Clock Out
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkerDashboard;
